import { useState, useMemo, useRef, useEffect } from 'react';
import clsx from 'clsx';
import { PaletteUtils } from '@shared/utils';
import { Icon } from '../icon';
import ChevronIcon from '@assets/icons/chevron.svg';

interface IMultiselectProps<T> {
  className?: string;
  wrapperClassName?: string;
  buttonClassName?: string;
  iconClassName?: string;
  items: { label: string; value: T }[] | string[];
  nullValue?: { isEnable: boolean; text?: string };
  value?: T[];
  onChange: (value?: T[]) => void;
  required?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
}

const defaultNullValueText = 'All';
export function Multiselect<T>(props: IMultiselectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    } else {
      document.removeEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  const nullText = `${props.nullValue?.text || defaultNullValueText}${props.required ? ' *' : ''}`;
  const currentLabel = useMemo(() => {
    if (props.isLoading) return 'Loading...';
    if (props.items.length === 0 && props.value?.length === 0) return undefined;
    if (Array.isArray(props.value) && props.value.length > 0) {
      return `Selected: ${props.value.length}`;
    }
    return undefined;
  }, [props.value, props.items, props.isLoading]);

  const handleSelectChange = (selectedValue: T) => {
    const currentValue = Array.isArray(props.value) ? props.value : [];
    const newValue = currentValue.includes(selectedValue) ? currentValue.filter((v) => v !== selectedValue) : [...currentValue, selectedValue];
    props.onChange(newValue);
  };

  return (
    <div ref={selectRef} className={clsx('w-full flex relative', props.disabled && 'opacity-60', props.className)}>
      <div
        className={clsx(
          'flex h-10 cursor-pointer w-full',
          'border border-transparent border-b-light hover:border hover:border-primary bg-ice hover:bg-white dark:bg-soft-black transition-all duration-200 ease-in-out',
          props.wrapperClassName,
        )}
        onClick={() => {
          if (props.disabled || props.isLoading) return;
          setIsOpen(!isOpen);
        }}
      >
        <div className='w-full py-2 px-3'>
          <p className='text-nowrap text-left text-soft-black dark:text-white font-light leading-normal w-full'>{currentLabel ?? nullText}</p>
        </div>
        <div
          className={clsx(
            'flex items-center justify-center w-10 h-10 min-w-10 min-h-10 relative -top-[1px] -right-[1px]',
            PaletteUtils.isInsigniaPalette && 'bg-soft-black dark:bg-slate',
            PaletteUtils.isVcfcPalette && 'bg-primary',
            props.buttonClassName,
          )}
        >
          <Icon icon={<ChevronIcon />} className={clsx('-rotate-90', props.iconClassName)} color={PaletteUtils.getColor('primary', 'white')} />
        </div>
      </div>
      {isOpen && (
        <div
          className={clsx(
            'flex no-scrollbar border flex-col absolute mt-10 w-full z-20 max-h-40 overflow-y-auto',
            PaletteUtils.isVcfcPalette && 'border-primary',
          )}
        >
          {props.nullValue?.isEnable && (
            <SelectItem
              label={props.nullValue.text ?? defaultNullValueText}
              isSelected={!props.value || (Array.isArray(props.value) && props.value.length === 0)}
              onChange={() => {
                props.onChange([]);
                setIsOpen(false);
              }}
            />
          )}
          {props.items.length === 0 ? (
            <div className='flex bg-white dark:bg-soft-black h-8 justify-center items-center'>
              <p className='font-light text-soft-black dark:text-white opacity-60'>No options</p>
            </div>
          ) : (
            props.items.map((x, key) => {
              const label = (x as any).label ?? x;
              const value = (x as any).value ?? x;
              const isSelected = Array.isArray(props.value) && props.value.includes(value);
              return (
                <SelectItem<T>
                  key={key}
                  value={value}
                  label={label}
                  isSelected={isSelected}
                  onChange={() => {
                    handleSelectChange(value);
                  }}
                />
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

interface ISelectItemProps<T> {
  value?: T;
  label: string;
  isSelected: boolean;
  onChange: () => void;
}

function SelectItem<T>(props: ISelectItemProps<T>) {
  return (
    <div
      onClick={props.onChange}
      className={clsx(
        'h-10 flex bg-white dark:bg-soft-black z-10 cursor-pointer',
        PaletteUtils.isInsigniaPalette && 'hover:bg-gray-silk dark:hover:bg-olive-gray',
        PaletteUtils.isVcfcPalette && 'hover:bg-primary-lightest dark:hover:bg-slate',
      )}
    >
      <p
        className={clsx(
          'h-6 my-2 mx-3 text-center font-light leading-normal text-ellipsis overflow-hidden',
          !props.isSelected && 'text-soft-black dark dark:text-white',
          props.isSelected && PaletteUtils.isInsigniaPalette && 'text-beaver dark:text-primary',
          props.isSelected && PaletteUtils.isVcfcPalette && 'text-primary',
        )}
      >
        {props.label}
      </p>
    </div>
  );
}
